import { useEffect, useRef, useState } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  QUICK_AMOUNTS,
  clampAmount,
  parseAmountInput,
  sanitizeAmountInput,
} from "./billAmountDiscount";

/** „Без" + бързите суми — сегментите под степера. */
const SEGMENT_AMOUNTS = [0, ...QUICK_AMOUNTS];
/** Стъпката на − / +. */
const STEP = 1;

interface BillAmountPickerProps {
  amount: number;
  onChange: (amount: number) => void;
  /** Таванът — сборът на редовете без заключена цена. */
  max: number;
  /** На десктоп полето се отваря фокусирано и селектирано. */
  autoFocus: boolean;
  isNarrow: boolean;
}

/** „Отстъпка на цялата сметка": заглавие + степер − [NN.NN €] + вдясно, под
 *  тях сегменти Без/5…50 € — същото подреждане като „Обща отстъпка" в %. */
export const BillAmountPicker = ({
  amount,
  onChange,
  max,
  autoFocus,
  isNarrow,
}: BillAmountPickerProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  // Докато се пише, полето държи текста както е („12,5"); извън фокус — сумата.
  const [draft, setDraft] = useState(amount.toFixed(2));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(amount.toFixed(2));
  }, [amount, focused]);

  useEffect(() => {
    if (!autoFocus) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 100);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  const handleInputChange = (raw: string) => {
    const { amount: parsed, draft: shown } = parseAmountInput(
      sanitizeAmountInput(raw),
    );
    if (parsed > max) {
      setDraft(max.toFixed(2));
      onChange(clampAmount(max, max));
      return;
    }
    setDraft(shown);
    onChange(clampAmount(parsed, max));
  };

  // Кегелът на сумата — като „NN%" в „Обща отстъпка"; на телефон ≥ 16px
  // (iOS Safari зуумва страницата при фокус в по-дребно поле).
  const amountTextClass = isNarrow ? "text-[18px]" : "text-[22px]";

  const hint = (
    <p className="m-0 mt-1 text-[12.5px] font-medium leading-[1.35] text-pos-muted">
      {t("itemDiscounts.billHint", { max: max.toFixed(2) })}
    </p>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-bold leading-[1.2] text-pos-ink">
            {t("itemDiscounts.billTitle")}
          </div>
          {!isNarrow && hint}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StepButton
            icon={<MinusOutlined />}
            onClick={() => onChange(clampAmount(amount - STEP, max))}
            disabled={amount <= 0}
            isNarrow={isNarrow}
          />
          {/* Сумата и „€" в една рамка, центрирани като „NN%" в „Обща
              отстъпка": полето е широко колкото текста си, „€" е на същата
              базова линия, а рамката расте за голяма сума; пишат се цифри и
              една запетая */}
          <label
            className={`flex items-center justify-center rounded-xl border border-pos-accent bg-pos-card text-pos-accent-ink transition-colors focus-within:ring-[3px] focus-within:ring-pos-accent/15 ${
              isNarrow ? "h-10 min-w-[104px] px-2" : "h-11 min-w-[124px] px-2.5"
            }`}
          >
            <span
              className={`inline-flex min-w-0 items-baseline gap-1 font-pos-mono font-bold leading-none ${amountTextClass}`}
            >
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={(e) => {
                  setFocused(true);
                  e.target.select();
                }}
                onBlur={() => setFocused(false)}
                inputMode="decimal"
                // Ширината е от текста (известен чак при писане) — `ch` на
                // моноширинния шрифт е точно един знак.
                style={{ width: `${Math.max(draft.length, 1)}ch` }}
                className={`min-w-0 border-0 bg-transparent p-0 text-center font-pos-mono font-bold leading-none text-pos-accent-ink outline-none ${amountTextClass}`}
              />
              <span>€</span>
            </span>
          </label>
          <StepButton
            icon={<PlusOutlined />}
            onClick={() => onChange(clampAmount(amount + STEP, max))}
            disabled={amount >= max}
            isNarrow={isNarrow}
          />
        </div>
      </div>
      {isNarrow && <div className="-mt-2">{hint}</div>}

      <div className="grid grid-cols-7 gap-1 rounded-xl bg-pos-line2 p-1">
        {SEGMENT_AMOUNTS.map((value) => {
          const isActive = Math.abs(amount - value) < 0.005;
          return (
            <button
              key={value}
              type="button"
              onClick={() => onChange(value)}
              disabled={value > max}
              className={`h-9 cursor-pointer rounded-[9px] border-0 px-0 font-pos-mono text-[13px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                isActive
                  ? "bg-pos-card text-pos-accent-ink shadow-sm"
                  : "bg-transparent text-pos-soft2 enabled:hover:text-pos-ink"
              }`}
            >
              {value === 0 ? t("itemDiscounts.none") : `${value} €`}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Същият бутон като в DiscountPercentPicker.
const StepButton = ({
  icon,
  onClick,
  disabled,
  isNarrow,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isNarrow: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex cursor-pointer items-center justify-center rounded-[10px] border border-pos-line bg-pos-card text-sm text-pos-soft2 transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:border-pos-accent/30 enabled:hover:bg-pos-marked enabled:hover:text-pos-accent-ink ${
      isNarrow ? "h-10 w-10" : "h-11 w-11"
    }`}
  >
    {icon}
  </button>
);
